import { z } from 'zod';

import { listResponse } from '../common/envelope';
import { Id, IsoDateTime } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const PlatformServiceStatusSchema = z.enum(['HEALTHY', 'DEGRADED', 'DOWN']);
export type PlatformServiceStatus = z.infer<typeof PlatformServiceStatusSchema>;

export const PlatformServiceTierSchema = z.enum(['CORE', 'COMMERCE', 'INTELLIGENCE', 'EXTENSION']);
export type PlatformServiceTier = z.infer<typeof PlatformServiceTierSchema>;

export const ServiceEventStatusSchema = z.enum([
  'QUEUED',
  'DELIVERED',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'SKIPPED',
]);
export type ServiceEventStatus = z.infer<typeof ServiceEventStatusSchema>;

/**
 * Canonical cross-service event kinds. Adding a new kind here is the only way to make
 * `eventBus.publishCore` (typed) accept it. Loose strings can still be published via the
 * lower-level bus for ad-hoc / internal events.
 */
export const CoreEventKindSchema = z.enum([
  'order.created',
  'order.confirmed',
  'order.shipped',
  'order.delivered',
  'order.cancelled',
  'payment.success',
  'payment.failed',
  'refund.approved',
  'refund.processed',
  'wallet.updated',
  'vendor.payout',
  'referral.rewarded',
  'cart.abandoned',
  'review.posted',
  'support.ticket.created',
  'support.ticket.assigned',
  'support.ticket.customer.replied',
  'support.ticket.vendor.replied',
  'support.ticket.admin.replied',
  'support.ticket.escalated',
  'support.ticket.resolved',
  'support.ticket.closed',
  'fraud.flagged',
  'pricing.signal',
]);
export type CoreEventKind = z.infer<typeof CoreEventKindSchema>;

// ── Platform service registry ──────────────────────────────────────────────

export const PlatformServiceSchema = z.object({
  id: Id,
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  tier: PlatformServiceTierSchema,
  status: PlatformServiceStatusSchema,
  endpointPrefixes: z.array(z.string()),
  ownedTables: z.array(z.string()),
  emitsEvents: z.array(z.string()),
  consumesEvents: z.array(z.string()),
  version: z.string().nullable(),
  lastHeartbeatAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type PlatformService = z.infer<typeof PlatformServiceSchema>;

export const RegisterServiceRequest = z
  .object({
    key: z
      .string()
      .trim()
      .regex(/^[a-z][a-z0-9_-]{1,40}$/, 'key must be lowercase slug'),
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(500).optional(),
    tier: PlatformServiceTierSchema,
    endpointPrefixes: z.array(z.string().trim().max(80)).max(40).optional(),
    ownedTables: z.array(z.string().trim().max(80)).max(60).optional(),
    emitsEvents: z.array(z.string().trim().max(80)).max(40).optional(),
    consumesEvents: z.array(z.string().trim().max(80)).max(40).optional(),
    version: z.string().trim().max(120).optional(),
  })
  .strict();
export type RegisterServiceRequest = z.infer<typeof RegisterServiceRequest>;

export const HeartbeatRequest = z
  .object({
    key: z.string().trim().min(1).max(60),
    status: PlatformServiceStatusSchema,
    version: z.string().trim().max(120).optional(),
  })
  .strict();
export type HeartbeatRequest = z.infer<typeof HeartbeatRequest>;

// ── Service events (durable log) ───────────────────────────────────────────

export const ServiceEventSchema = z.object({
  id: Id,
  kind: z.string(),
  contentHash: z.string(),
  sourceService: z.string(),
  subjectId: z.string(),
  payload: z.unknown(),
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  status: ServiceEventStatusSchema,
  occurredAt: IsoDateTime,
  processedAt: IsoDateTime.nullable(),
  error: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
});
export type ServiceEvent = z.infer<typeof ServiceEventSchema>;

export const PublishEventRequest = z
  .object({
    kind: z.string().trim().min(4).max(80),
    sourceService: z.string().trim().min(1).max(60),
    subjectId: z.string().trim().min(1).max(120),
    refType: z.string().trim().max(80).optional(),
    refId: z.string().trim().max(120).optional(),
    payload: z.record(z.unknown()).optional(),
  })
  .strict();
export type PublishEventRequest = z.infer<typeof PublishEventRequest>;

export const ListEventsQuery = z
  .object({
    kind: z.string().optional(),
    sourceService: z.string().optional(),
    subjectId: z.string().optional(),
    status: ServiceEventStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
  })
  .strict();
export type ListEventsQuery = z.infer<typeof ListEventsQuery>;

// ── Readiness / status page ────────────────────────────────────────────────

export const ReadinessReportSchema = z.object({
  /// Overall verdict: HEALTHY if every CORE service is healthy, else DEGRADED / DOWN.
  status: PlatformServiceStatusSchema,
  generatedAt: IsoDateTime,
  services: z.array(
    z.object({
      key: z.string(),
      tier: PlatformServiceTierSchema,
      status: PlatformServiceStatusSchema,
      lastHeartbeatAt: IsoDateTime.nullable(),
      /// Minutes since last heartbeat — `null` if never reported.
      staleMinutes: z.number().int().nullable(),
    }),
  ),
});
export type ReadinessReport = z.infer<typeof ReadinessReportSchema>;

// ── System Audit Logs ───────────────────────────────────────────────────────

export const AuditLogActorSchema = z.object({
  name: z.string(),
  email: z.string(),
});
export type AuditLogActor = z.infer<typeof AuditLogActorSchema>;

export const AuditLogSchema = z.object({
  id: Id,
  actorId: z.string().nullable(),
  actor: AuditLogActorSchema.nullable().optional(),
  agentId: z.string().nullable(),
  actorRole: z.string().nullable(),
  action: z.string(),
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  ip: z.string().nullable(),
  requestId: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AuditLog = z.infer<typeof AuditLogSchema>;

export const ListAuditLogsQuery = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
    q: z.string().trim().max(200).optional(),
    action: z.string().optional(),
    entityType: z.string().optional(),
    actorId: z.string().optional(),
    actorRole: z.string().optional(),
    module: z.string().optional(),
    ip: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    exportCsv: z.preprocess((v) => v === 'true' || v === '1' || v === true, z.boolean()).optional(),
  })
  .strict();
export type ListAuditLogsQuery = z.infer<typeof ListAuditLogsQuery>;

export const ListAuditLogsResponse = listResponse(AuditLogSchema);
export type ListAuditLogsResponse = z.infer<typeof ListAuditLogsResponse>;
